import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import StatusBar from './PlayStatusBar.js';
import './PartyPanel.css';

export function PartyVitals({ vitals = {}, stale = false }) {
  const pools = [['Health', 'currentHp', 'maxHp', 'play-health-bar'], ['Stamina', 'currentStamina', 'maxStamina', 'play-stamina-bar'],
    ['Mana', 'currentPp', 'maxPp', 'play-magic-bar']];
  return <div className={`play2-vital-rail${stale ? ' party-stale' : ''}`} aria-label={stale ? 'Last known condition' : 'Companion condition'}>
    {pools.map(([label, current, maximum, className]) => {
      const value = vitals[current], max = vitals[maximum];
      if (label === 'Mana' && !(Number.isFinite(max) && max > 0)) return null;
      const known = Number.isFinite(value) && Number.isFinite(max) && max > 0;
      return <StatusBar key={label} label={label} value={value} max={max} className={className} known={known} stale={stale} />;
    })}
  </div>;
}

export default function PartyPanel({ parties = [], playerId, renderMember, renderPlayer, onInstruction, disabled }) {
  const [expanded, setExpanded] = useState(null);
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const openPartyId = parties.find(party => party.planId === expanded)?.planId;

  useEffect(() => {
    if (!openPartyId) return undefined;
    const trigger = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const handleKey = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setExpanded(null);
      }
      if (event.key === 'Tab') {
        const buttons = Array.from(dialogRef.current.querySelectorAll('button:not(:disabled)'));
        const first = buttons[0], last = buttons[buttons.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = overflow;
      trigger?.focus();
    };
  }, [openPartyId]);

  if (!parties.length) return null;
  return <div className="party-groups">{parties.map(party => <section className="party-group" key={party.planId} aria-label="Current party">
    <button type="button" className="party-agreement-tab" aria-haspopup="dialog" aria-expanded={expanded === party.planId}
      onClick={() => setExpanded(expanded === party.planId ? null : party.planId)}>Agreement</button>
    {expanded === party.planId && createPortal(<div className="party-agreement-overlay"
      onClick={event => { if (event.target === event.currentTarget) setExpanded(null); }}>
      <section ref={dialogRef} className="party-agreement" role="dialog" aria-modal="true" aria-label="Party agreement">
      <div className="party-agreement-header"><h3>Party agreement</h3>
        <button ref={closeRef} type="button" className="secondary-button" onClick={() => setExpanded(null)}>Close</button></div>
      <p><strong>{party.objective}</strong></p>
      <p>{party.termsText}</p><p>{party.coordinationTerms}</p>
      {party.endsAtMinute != null && <p>Agreed end: {Math.floor((party.endsAtMinute % 1440) / 60).toString().padStart(2, '0')}:{Math.floor(party.endsAtMinute % 60).toString().padStart(2, '0')}</p>}
      <div className="party-actions">{[
        ['Ask to wait', 'I ask my party to wait for me.'], ['Regroup', 'I ask my party to regroup with me.'],
        ['Leave party', 'I tell my party that I am leaving our agreed outing.'],
      ].map(([label, instruction]) => <button type="button" className="secondary-button" key={label} disabled={disabled}
        onClick={() => { setExpanded(null); onInstruction(instruction); }}>{label}</button>)}</div>
      </section>
    </div>, document.body)}
    <div className="party-cards">{party.members.map(member => member.actorId === playerId
      ? <div key={member.actorId}>{renderPlayer()}</div> : <div key={member.actorId}>{renderMember(member, party)}</div>)}</div>
  </section>)}</div>;
}

const materialAge = (minute, currentMinute) => {
  if (Number.isFinite(currentMinute) && Number.isFinite(minute)) {
    const age = Math.max(0, Math.floor(currentMinute - minute));
    return age < 1 ? 'just now' : age < 60 ? `${age} minutes ago` : age < 1440 ? `${Math.floor(age / 60)} hours ago` : `${Math.floor(age / 1440)} days ago`;
  }
  return Number.isFinite(minute) ? `at ${Math.floor(((minute % 1440) + 1440) % 1440 / 60).toString().padStart(2, '0')}:${Math.floor(((minute % 60) + 60) % 60).toString().padStart(2, '0')}` : 'at an unknown time';
};

export function HarvestInventory({ batches = [], onInstruction, disabled, currentMinute }) {
  if (!batches.length) return null;
  return <details className="harvest-inventory"><summary>Gathered materials ({batches.length})</summary>
    <ul>{batches.map(batch => <li key={batch.itemId}>
      <strong>{batch.label || 'Gathered material'}</strong>: {batch.quantity} {batch.unit} · {batch.condition || 'Unchecked'}
      {batch.usableQuantity != null && batch.usableQuantity < batch.quantity && <span> ({batch.usableQuantity} usable)</span>}
      <span> · {batch.storageLabel || 'Carried'} · gathered {materialAge(batch.harvestedAtMinute, currentMinute)}</span>
      <span> · last checked {materialAge(batch.assessedThroughMinute, currentMinute)}</span>
      <button type="button" className="secondary-button" disabled={disabled} onClick={() =>
        onInstruction(`I inspect the condition of the gathered material ${batch.itemId}.`)}>Inspect condition</button>
    </li>)}</ul>
  </details>;
}
